import { useIntl } from '@leagueplatform/locales';
import {
  ParagraphText,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { SectionCard } from 'components/section-card/section-card.component';
import { SectionHeading } from 'components/section-heading/section-heading.component';
import { PreferenceToggle } from 'components/preference-toggle/preference-toggle.component';
import { LoadingSpinner } from 'components/loading-spinner/loading-spinner.component';
import { useEmailNotificationPreference } from './use-email-notification-preference.hook';

// Title + caption shown to the left of the toggle. Rendered inside the Switch's
// <label>, so these are spans set to `display: block` (so the title and caption
// stack); a block-level element like a <div> would be invalid markup inside the
// label's text wrapper. Typography is Notifications-specific per Figma
// ($subtitleTwo title, Text-Primary caption); kept local so each screen owns its
// own type tokens while sharing the Switch wiring via PreferenceToggle.
const ToggleLabel = ({
  label,
  caption,
}: {
  label: string;
  caption: string;
}) => (
  <>
    <UtilityText
      css={{
        display: 'block',
        typography: '$subtitleTwo',
        color: '$onSurfaceTextPrimary',
      }}
    >
      {label}
    </UtilityText>
    <UtilityText
      css={{
        display: 'block',
        marginBlockStart: '$half',
        typography: '$caption',
        color: '$onSurfaceTextPrimary',
      }}
    >
      {caption}
    </UtilityText>
  </>
);

export const NotificationSettingsCard = () => {
  const { formatMessage } = useIntl();
  const { isEnabled, isLoading, isError, isSaving, hasSaveError, toggle } =
    useEmailNotificationPreference();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <ParagraphText role="alert" css={{ typography: '$bodyOne' }}>
        {formatMessage({ id: 'STR_ERROR_STATE_BODY_API' })}
      </ParagraphText>
    );
  }

  return (
    <SectionCard>
      <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
        <SectionHeading>
          {formatMessage({ id: 'STR_NOTIFICATIONS_APP_HEADING' })}
        </SectionHeading>

        <StackLayout spacing="$one" horizontalAlignment="stretch">
          <PreferenceToggle
            id="allow-email-notifications"
            label={
              <ToggleLabel
                label={formatMessage({ id: 'STR_NOTIFICATIONS_ALLOW_EMAIL' })}
                caption={formatMessage({
                  id: 'STR_NOTIFICATIONS_ALLOW_EMAIL_DESCRIPTION',
                })}
              />
            }
            checked={isEnabled}
            onToggle={toggle}
            disabled={isSaving}
            alignTop
          />

          {hasSaveError && (
            <UtilityText
              role="alert"
              css={{ typography: '$caption', color: '$onSurfaceTextCritical' }}
            >
              {formatMessage({ id: 'STR_NOTIFICATIONS_UPDATE_ERROR' })}
            </UtilityText>
          )}
        </StackLayout>
      </StackLayout>
    </SectionCard>
  );
};
